import { DateTime } from "luxon";
import { OptimizedGantt } from "@planzone/dto";
import {
  Event,
  event,
  Gantt,
  Identifiable,
  list as listMock,
  milestone,
  Task,
  task,
  task as taskMock,
} from "@planzone/dto";
import {
  useEditEvent,
  useListEdition,
  useMilestoneEdition,
  useEditTask,
} from "@planzone/view-models";
import { useProjectCtx } from "../../../../projectCtx";
import { isEvent, isList, isMilestone, isProject, isTask } from "../utils";

// Round Y/M/D/H/M in user TZ to UTC ISO, dropping seconds/ms. Matches the
// pre-migration native-Date round-trip (new Date(y,m,d,h,m).toISOString())
// which incidentally truncated sub-minute precision via local-TZ
// reconstruction; the Gantt's edit convention is user TZ end-to-end.
function toEventTimeIso(iso: string, userTimezone: string): string {
  const dt = DateTime.fromISO(iso, { zone: "utc" }).setZone(userTimezone);
  return (
    DateTime.fromObject(
      {
        year: dt.year,
        month: dt.month,
        day: dt.day,
        hour: dt.hour,
        minute: dt.minute,
      },
      { zone: userTimezone },
    )
      .toUTC()
      .toISO() ?? ""
  );
}

function useElementEdition(element: OptimizedGantt[number]) {
  const { project } = useProjectCtx();
  const userTimezone = DateTime.local().zoneName;
  const { isRequestPending: isTaskEditionRequestPending, editTask } =
    useEditTask({ ...task, ...element });
  const { isRequestPending: isListEditionRequestPending, editList } =
    useListEdition();
  const { isRequestPending: isMilestoneEditionRequestPending, editMilestone } =
    useMilestoneEdition({ ...milestone, ...element });
  const { isRequestPending: isEventEditionRequestPending, editEvent } =
    useEditEvent({ ...event, ...element });

  return {
    isEditionRequestPending: isTask(element)
      ? isTaskEditionRequestPending
      : isList(element)
        ? isListEditionRequestPending
        : isMilestone(element)
          ? isMilestoneEditionRequestPending
          : isEvent(element)
            ? isEventEditionRequestPending
            : false,
    editElement: (nextElement: OptimizedGantt[number]) => {
      return isTask(element)
        ? !isTaskEditionRequestPending &&
            editTask({
              nextTask: {
                ...taskMock,
                ...element,
                activityKeyRank: nextElement.keyRank,
                startDate: nextElement.startDate,
                endDate: nextElement.endDate,
              },
              task: {
                ...taskMock,
                ...element,
                activityKeyRank: element.keyRank,
                // percentComplete: element.progress,
              },
            })
        : isMilestone(element)
          ? !isMilestoneEditionRequestPending &&
            editMilestone({
              nextMilestone: {
                ...milestone,
                ...element,
                keyRank: nextElement.keyRank,
                endDate: nextElement.endDate,
              },
              milestone: { ...milestone, ...element },
            })
          : isEvent(element)
            ? !isEventEditionRequestPending &&
              editEvent({
                nextEvent: {
                  ...event,
                  ...element,
                  startTime: toEventTimeIso(
                    nextElement.startDate,
                    userTimezone,
                  ),
                  endTime: toEventTimeIso(nextElement.endDate, userTimezone),
                },
                event: { ...event, ...element },
              })
            : isList(element)
              ? !isListEditionRequestPending &&
                editList({
                  list: { ...listMock, ...element },
                  nextList: {
                    ...listMock,
                    ...element,
                    startDate: nextElement.startDate,
                    endDate: nextElement.endDate,
                  },
                })
              : isProject(element) &&
                !isListEditionRequestPending &&
                editList({
                  list: {
                    ...listMock,
                    ...project.rootList,
                  },
                  nextList: {
                    ...listMock,
                    ...project.rootList,
                    startDate: nextElement.startDate,
                    endDate: nextElement.endDate,
                  },
                });
    },
  };
}

export { useElementEdition };
